import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { CreditCard, AlertCircle, Loader, Shield } from 'lucide-react';

// Stripe publishable key from environment variable
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 
  'pk_live_51SqYrgCGDZNqbV2qvVHGfGwCobudUm7r98Gff8XzEmMLubIUft4O2i3GiP2tIl078QrYtjNBx1LKNrhbIy9nYWze00Oem8ibWV'
);

interface PaymentFormProps {
  requestId: string;
  onSuccess: (paymentIntentId: string, amount: number, discountCode?: string) => void;
  onFreeConsultation: (amount: number, discountCode?: string) => void;
  onCancel?: () => void;
}

// Card Element styling
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#4A4A4A',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      '::placeholder': {
        color: '#8B8B8B',
      },
      padding: '12px',
    },
    invalid: {
      color: '#dc2626',
      iconColor: '#dc2626',
    },
  },
};

const CheckoutForm: React.FC<PaymentFormProps> = ({ 
  requestId,
  onSuccess,
  onFreeConsultation,
  onCancel 
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [discountApplied, setDiscountApplied] = useState<{code: string, discount: number} | null>(null);
  const [originalAmount] = useState(1999);
  const [finalAmount, setFinalAmount] = useState(1999);

  // Check if payment is free (100% discount)
  const isFree = finalAmount === 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isFree) {
      onFreeConsultation(finalAmount, discountApplied?.code);
      return;
    }

    if (!stripe || !elements) {
      setError('Stripe has not loaded yet. Please wait...');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card element not found. Please refresh and try again.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const response = await fetch(
        'https://0guzh8ema9.execute-api.us-east-1.amazonaws.com/prod/payments/intents/hold',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            amount: finalAmount,
            currency: 'usd',
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create payment hold');
      }

      const data = await response.json();
      
      if (data.free === true) {
        onFreeConsultation(finalAmount, discountApplied?.code);
        return;
      }
      
      const { clientSecret } = data;
      console.log('💳 Payment hold created, authorizing...');
      
      // Use confirmCardPayment with CardElement to authorize the hold
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
          },
        }
      );

      if (confirmError) {
        console.error('❌ Payment authorization error:', confirmError);
        setError(confirmError.message || 'Payment authorization failed. Please try again.');
        setIsProcessing(false);
      } else if (paymentIntent) {
        console.log('✅ Payment hold authorized:', paymentIntent.id, 'Status:', paymentIntent.status);
        
        if (paymentIntent.status === 'requires_capture') {
          // Hold successful - backend webhook will update request status
          console.log('✅ Hold placed successfully. Backend will match with doctor.');
          onSuccess(paymentIntent.id, finalAmount, discountApplied?.code);
        } else {
          setError(`Payment status: ${paymentIntent.status}. Please contact support.`);
          setIsProcessing(false);
        }
      }
    } catch (err: any) {
      console.error('❌ Payment exception:', err);
      setError(err.message || 'Payment failed. Please try again or contact support.');
      setIsProcessing(false);
    }
  };

  const handleCardChange = (event: any) => {
    setCardComplete(event.complete);
    if (event.error) {
      setError(event.error.message);
    } else {
      setError(null);
    }
  };

  const validateDiscountCode = async () => {
    if (!discountCode.trim()) return;
    
    setIsValidatingCode(true);
    setError(null);
    
    try {
      const response = await fetch(
        'https://0guzh8ema9.execute-api.us-east-1.amazonaws.com/prod/payments/validate-discount',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            code: discountCode.trim().toUpperCase()
            // amount is optional - backend doesn't require it
          })
        }
      );
      
      const data = await response.json();
      
      if (response.ok && data.valid) {
        // Calculate discount based on percentage from API
        const discountAmount = Math.round(originalAmount * (data.discountPercent / 100));
        const newAmount = originalAmount - discountAmount;
        setFinalAmount(newAmount);
        setDiscountApplied({ code: discountCode.toUpperCase(), discount: data.discountPercent });
        setError(null);
      } else {
        setError(data.message || 'Invalid discount code');
        setDiscountApplied(null);
        setFinalAmount(originalAmount);
      }
    } catch (err) {
      console.error('Discount validation error:', err);
      setError('Unable to validate discount code. Please try again.');
      setDiscountApplied(null);
      setFinalAmount(originalAmount);
    } finally {
      setIsValidatingCode(false);
    }
  };

  const removeDiscount = () => {
    setDiscountCode('');
    setDiscountApplied(null);
    setFinalAmount(originalAmount);
    setError(null);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Discount Code Section */}
      <div className="bg-gradient-to-r from-[#FFF1F2] to-[#F0F9FF] border border-[#38BDF8]/20 rounded-xl p-5">
        <label className="block text-sm font-semibold text-[#334155] mb-3 flex items-center">
          <svg className="w-5 h-5 text-[#38BDF8] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
          Have a discount code?
        </label>
        {!discountApplied ? (
          <div className="flex gap-2">
            <input
              type="text"
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
              placeholder="ENTER CODE"
              className="flex-1 px-4 py-3 border-2 border-[#334155]/20 rounded-xl focus:ring-2 focus:ring-[#38BDF8] focus:border-[#38BDF8] transition-all bg-white uppercase font-semibold text-[#334155] placeholder:text-[#334155]/40"
              disabled={isValidatingCode}
            />
            <button
              type="button"
              onClick={validateDiscountCode}
              disabled={!discountCode.trim() || isValidatingCode}
              className={`px-8 py-3 rounded-xl font-bold transition-all ${
                discountCode.trim() && !isValidatingCode
                  ? 'bg-[#38BDF8] text-white hover:bg-[#0EA5E9] shadow-md hover:shadow-lg'
                  : 'bg-[#334155]/10 text-[#334155]/40 cursor-not-allowed'
              }`}
            >
              {isValidatingCode ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                'Apply'
              )}
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <span className="text-green-900 font-bold text-lg">{discountApplied.code}</span>
                <p className="text-green-700 text-sm font-medium">{discountApplied.discount}% discount applied!</p>
              </div>
            </div>
            <button
              type="button"
              onClick={removeDiscount}
              className="text-red-600 hover:text-red-700 text-sm font-semibold hover:bg-red-50 px-3 py-1 rounded-lg transition-colors"
            >
              Remove
            </button>
          </div>
        )}
      </div>

      {/* Price Display */}
      <div className="bg-[#F0F9FF] rounded-xl p-4">
        {discountApplied ? (
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-[#334155]/60">
              <span>Original price:</span>
              <span className="line-through">${(originalAmount / 100).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm text-green-600">
              <span>Discount ({discountApplied.discount}%):</span>
              <span>-${((originalAmount - finalAmount) / 100).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold text-[#334155] pt-2 border-t border-[#334155]/10">
              <span>Total:</span>
              <span className={isFree ? "text-green-600" : "text-[#38BDF8]"}>
                {isFree ? 'FREE!' : `$${(finalAmount / 100).toFixed(2)}`}
              </span>
            </div>
          </div>
        ) : (
          <div className="flex justify-between text-lg font-bold text-[#334155]">
            <span>Total:</span>
            <span className="text-[#38BDF8]">${(finalAmount / 100).toFixed(2)}</span>
          </div>
        )}
      </div>

      {/* Card Input - Hide if free */}
      {!isFree && (
        <div>
          <label className="block text-sm font-semibold text-[#334155] mb-3">
            Card Information
          </label>
          <div className="border-2 border-[#334155]/20 rounded-xl p-4 bg-white hover:border-[#38BDF8] transition-colors focus-within:border-[#38BDF8] focus-within:ring-4 focus-within:ring-[#38BDF8]/10">
            <CardElement 
              options={cardElementOptions} 
              onChange={handleCardChange}
            />
          </div>
          <div className="flex items-center gap-2 mt-2 text-xs text-[#334155]/60">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            <span>Your card details are encrypted and never stored</span>
          </div>
        </div>
      )}

      {/* Free Consultation Message */}
      {isFree && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
          <div className="text-4xl mb-3">🎉</div>
          <h3 className="text-xl font-bold text-green-900 mb-2">Free Consultation!</h3>
          <p className="text-green-700">
            Your discount code covers the full amount. No payment required!
          </p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Buttons */}
      <div className="flex gap-4 pt-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isProcessing}
            className="flex-1 py-4 px-6 border-2 border-[#334155]/20 text-[#334155] rounded-xl font-semibold hover:bg-[#F0F9FF] hover:border-[#38BDF8]/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={!isFree && (!stripe || isProcessing || !cardComplete)}
          className={`flex-1 py-4 px-6 rounded-xl font-bold text-lg flex items-center justify-center transition-all shadow-lg ${
            (!isFree && (!stripe || isProcessing || !cardComplete)) 
              ? 'bg-[#334155]/20 text-[#334155]/40 cursor-not-allowed shadow-none' 
              : 'bg-gradient-to-r from-[#0EA5E9] to-[#38BDF8] text-white hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]'
          }`}
        >
          {isProcessing ? (
            <>
              <Loader className="w-6 h-6 mr-2 animate-spin" />
              Processing...
            </>
          ) : isFree ? (
            <>
              <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Complete Free Booking
            </>
          ) : (
            <>
              <Shield className="w-6 h-6 mr-2" />
              Authorize ${(finalAmount / 100).toFixed(2)} Hold
            </>
          )}
        </button>
      </div>
      
      {/* Money Back Guarantee */}
      {!isFree && (
        <div className="mt-4 text-center">
          <p className="text-sm text-[#334155]/70">
            💯 100% Satisfaction Guaranteed • Secure Checkout
          </p>
        </div>
      )}
    </form>
  );
};

// Main Payment Form Component
const PaymentForm: React.FC<PaymentFormProps> = ({
  requestId,
  onSuccess,
  onFreeConsultation,
  onCancel
}) => {
  return (
    <div className="max-w-2xl mx-auto">
      {/* Trust Header */}
      <div className="bg-gradient-to-br from-[#0EA5E9] to-[#38BDF8] rounded-2xl p-8 mb-6 text-white shadow-xl">
        <div className="flex items-center justify-center mb-4">
          <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
            <Shield className="w-10 h-10 text-white" />
          </div>
        </div>
        <h2 className="text-3xl font-bold text-center mb-2">Secure Payment</h2>
        <p className="text-center text-white/90 text-lg mb-6">
          Complete your consultation booking
        </p>
        
        {/* Price Badge */}
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 text-center">
          <p className="text-white/80 text-sm mb-2">Consultation Fee</p>
          <p className="text-5xl font-bold mb-1">$19.99</p>
          <p className="text-white/70 text-sm">One-time payment • No hidden fees</p>
        </div>
      </div>

      {/* Trust Badges */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-[#334155]/10 rounded-xl p-4 text-center shadow-sm">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <p className="text-xs font-semibold text-[#334155]">SSL Encrypted</p>
        </div>
        
        <div className="bg-white border border-[#334155]/10 rounded-xl p-4 text-center shadow-sm">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
          </div>
          <p className="text-xs font-semibold text-[#334155]">Stripe Powered</p>
        </div>
        
        <div className="bg-white border border-[#334155]/10 rounded-xl p-4 text-center shadow-sm">
          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
            <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <p className="text-xs font-semibold text-[#334155]">PCI Compliant</p>
        </div>
      </div>

      {/* Payment Form Card */}
      <div className="bg-white rounded-2xl shadow-xl border border-[#334155]/10 p-8">
        {/* Payment Hold Notice */}
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-xl p-5 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-blue-900 mb-2">How Payment Works</h3>
              <ul className="space-y-1.5 text-sm text-blue-800">
                <li className="flex items-start">
                  <span className="mr-2">1️⃣</span>
                  <span>We'll place a <strong>temporary hold</strong> on your card</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">2️⃣</span>
                  <span>A doctor will review and accept your request</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">3️⃣</span>
                  <span>You'll <strong>only be charged</strong> when a doctor confirms</span>
                </li>
              </ul>
              <p className="text-xs text-blue-700 mt-3 font-medium">
                💡 If no doctor accepts, the hold will be released automatically
              </p>
            </div>
          </div>
        </div>

        {/* What You Get */}
        <div className="bg-[#F0F9FF] border border-[#38BDF8]/20 rounded-xl p-5 mb-6">
          <h3 className="font-bold text-[#334155] mb-3 flex items-center">
            <svg className="w-5 h-5 text-[#38BDF8] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            What's Included
          </h3>
          <ul className="space-y-2 text-sm text-[#334155]">
            <li className="flex items-start">
              <span className="text-[#38BDF8] mr-2">✓</span>
              <span>20-minute video consultation with a qualified doctor</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#38BDF8] mr-2">✓</span>
              <span>Professional medical guidance and advice</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#38BDF8] mr-2">✓</span>
              <span>Google Meet link sent to your email</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#38BDF8] mr-2">✓</span>
              <span>Medical certificate available (if needed)</span>
            </li>
          </ul>
        </div>

        {/* Stripe Elements */}
        <Elements stripe={stripePromise}>
          <CheckoutForm 
            requestId={requestId}
            onSuccess={onSuccess}
            onFreeConsultation={onFreeConsultation}
            onCancel={onCancel} 
          />
        </Elements>
      </div>

      {/* Security Footer */}
      <div className="mt-6 text-center">
        <div className="inline-flex items-center gap-2 text-sm text-[#334155]/60">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
          </svg>
          <span>Your payment information is encrypted and secure</span>
        </div>
        <p className="text-xs text-[#334155]/50 mt-2">
          Powered by Stripe • We never store your card details
        </p>
      </div>
    </div>
  );
};

export default PaymentForm;
